import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Space, Typography } from 'antd';
import { 
  DashboardOutlined,
  TeamOutlined,
  UserAddOutlined,
  TrophyOutlined,
  BookOutlined,
  CalendarOutlined,
  FolderOutlined,
  DollarOutlined,
  BarChartOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BankOutlined,
  HeartOutlined,
  ApartmentOutlined,
  SmileOutlined,
  CustomerServiceOutlined,
  ApiOutlined,
  WarningOutlined,
  LogoutOutlined as ExitOutlined,
  ClockCircleOutlined,
  ApartmentOutlined as OrgOutlined,
  LockOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api/apiService';
import NotificationBell from '../Notifications/NotificationBell';
import type { MenuProps } from 'antd';
import './Layout.css';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

// Sidebar category grouping: category -> ordered member route keys.
const SIDEBAR_GROUPS: { key: string; label: string; icon: React.ReactNode; members: string[] }[] = [
  { key: 'grp-people', label: 'People', icon: <TeamOutlined />, members: ['/employees', '/onboarding', '/org-chart', '/documents'] },
  { key: 'grp-talent', label: 'Talent', icon: <TrophyOutlined />, members: ['/recruitment', '/performance', '/training', '/succession'] },
  { key: 'grp-time', label: 'Time & Leave', icon: <ClockCircleOutlined />, members: ['/attendance', '/leave'] },
  { key: 'grp-pay', label: 'Payroll & Benefits', icon: <DollarOutlined />, members: ['/payroll', '/benefits', '/compensation', '/statutory'] },
  { key: 'grp-experience', label: 'Employee Experience', icon: <SmileOutlined />, members: ['/engagement', '/helpdesk', '/disciplinary', '/exits'] },
  { key: 'grp-analytics', label: 'Analytics', icon: <BarChartOutlined />, members: ['/analytics', '/reports'] },
  { key: 'grp-admin', label: 'Administration', icon: <SettingOutlined />, members: ['/clients', '/integrations', '/settings'] },
];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bmpLinked, setBmpLinked] = useState<boolean>(() => { try { return localStorage.getItem('hr_source') === 'nexus_linked'; } catch (e) { return false; } });
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r: any = await apiService.getNexusBMPStatus();
        if (active && r && r.success && (r.enabled === true || (r.data && Number(r.data.bmp_users) > 0))) setBmpLinked(true);
      } catch (e) { /* keep current value */ }
    })();
    return () => { active = false; };
  }, []);

  // HR Modules Menu Items with Role-Based Access
  const getMenuItems = (): MenuProps['items'] => {
    const allItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/employees',
        icon: <TeamOutlined />,
        label: 'Employee Management',
        roles: ['hr_manager', 'admin', 'hr_consultant', 'hr_consultant_admin', 'superadmin']
      },
      {
        key: '/recruitment',
        icon: <UserAddOutlined />,
        label: 'Recruitment & Hiring',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/onboarding',
        icon: <UserOutlined />,
        label: 'Employee Onboarding',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/performance',
        icon: <TrophyOutlined />,
        label: 'Performance Management',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/training',
        icon: <BookOutlined />,
        label: 'Training & Development',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/leave',
        icon: <CalendarOutlined />,
        label: 'Leave Management',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/documents',
        icon: <FolderOutlined />,
        label: 'Document Management',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/payroll',
        icon: <DollarOutlined />,
        label: 'Payroll',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/benefits',
        icon: <HeartOutlined />,
        label: 'Benefits',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/analytics',
        icon: <BarChartOutlined />,
        label: 'Analytics & Reports',
        roles: ['hr_manager', 'admin', 'superadmin'],
        children: [
          { key: '/analytics', label: 'Analytics Dashboard', roles: ['hr_manager', 'admin', 'superadmin'] },
          { key: '/reports', label: 'Reports & Exports', roles: ['hr_manager', 'admin', 'superadmin'] },
        ]
      },
      {
        key: '/clients',
        icon: <ApartmentOutlined />,
        label: 'Client Management',
        roles: ['hr_consultant', 'hr_consultant_admin', 'superadmin']
      },
      {
        key: '/engagement',
        icon: <SmileOutlined />,
        label: 'Employee Engagement',
        roles: ['hr_manager', 'admin', 'superadmin', 'hr_consultant', 'hr_consultant_admin']
      },
      {
        key: '/compensation',
        icon: <DollarOutlined />,
        label: 'Compensation Management',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/succession',
        icon: <ApartmentOutlined />,
        label: 'Succession Planning',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/helpdesk',
        icon: <CustomerServiceOutlined />,
        label: 'HR Helpdesk',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin', 'hr_consultant', 'hr_consultant_admin']
      },
      {
        key: '/statutory',
        icon: <BankOutlined />,
        label: 'Statutory Remittances',
        roles: ['admin', 'superadmin']
      },
      {
        key: '/integrations',
        icon: <ApiOutlined />,
        label: 'Integrations',
        roles: ['admin', 'superadmin']
      },
      {
        key: '/disciplinary',
        icon: <WarningOutlined />,
        label: 'Disciplinary & Grievance',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/exits',
        icon: <ExitOutlined />,
        label: 'Exit Management',
        roles: ['hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/attendance',
        icon: <ClockCircleOutlined />,
        label: 'Attendance & Shifts',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/org-chart',
        icon: <OrgOutlined />,
        label: 'Organisation Chart',
        roles: ['staff', 'hr_manager', 'admin', 'superadmin']
      },
      {
        key: '/settings',
        icon: <SettingOutlined />,
        label: 'Settings & Administration',
        roles: ['admin', 'superadmin']
      }
    ];

    // Flatten to the leaf items this role can see (expanding any existing sub-children).
    const visible: Record<string, string> = {};
    allItems
      .filter(item => user && item.roles.includes(user.role))
      .forEach((item: any) => {
        if (item.children) {
          item.children
            .filter((c: any) => user && c.roles.includes(user.role))
            .forEach((c: any) => { visible[c.key] = c.label; });
        } else {
          visible[item.key] = item.label;
        }
      });

    const result: MenuProps['items'] = [];
    if (visible['/dashboard']) {
      result.push({ key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' });
    }
    SIDEBAR_GROUPS.forEach(g => {
      const children = g.members.filter(m => visible[m]).map(m => ({ key: m, label: visible[m] }));
      if (children.length) result.push({ key: g.key, icon: g.icon, label: g.label, children });
    });
    // Safety net: surface any visible route not assigned to a group.
    const assigned = new Set<string>(['/dashboard', ...SIDEBAR_GROUPS.flatMap(g => g.members)]);
    Object.keys(visible).forEach(k => { if (!assigned.has(k)) result.push({ key: k, label: visible[k] }); });
    return result;
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'lock',
      icon: <LockOutlined />,
      label: 'Lock Screen',
      onClick: () => {
        sessionStorage.setItem('nexus_screen_locked', '1');
        window.location.reload();
      }
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        logout();
        navigate('/login');
      }
    }
  ];

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      'superadmin': '#722ed1',
      'admin': '#1890ff',
      'hr_manager': '#52c41a',
      'staff': '#faad14'
    };
    return colors[role] || '#d9d9d9';
  };

  const getRoleDisplayName = (role: string) => {
    const names: Record<string, string> = {
      'superadmin': 'Super Admin',
      'admin': 'Administrator',
      'hr_manager': 'HR Manager',
      'staff': 'Employee'
    };
    return names[role] || role;
  };

  return (
    <AntLayout className="layout-container">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        className="layout-sider"
        width={260}
        collapsedWidth={80}
        style={{ overflow: 'auto', height: '100vh', position: 'sticky', top: 0, left: 0 }}
      >
        {/* Logo */}
        <div className="layout-logo" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          {collapsed ? (
            <img src="/logo-icon.svg" alt="Nexus HR" style={{ width: 40, height: 40 }} />
          ) : (
            <img src="/logo-dark.svg" alt="Nexus HR" style={{ width: 160, height: 'auto' }} />
          )}
        </div>

        {/* Navigation Menu */}
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={SIDEBAR_GROUPS.filter(g => g.members.includes(location.pathname)).map(g => g.key)}
          items={getMenuItems()}
          onClick={handleMenuClick}
          className="layout-menu"
        />

        {/* Organization Info */}
        {!collapsed && user && (
          <div className="organization-info">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Organization: {user.organization_name || user.tenant_id}
            </Text>
          </div>
        )}
      </Sider>

      <AntLayout className="layout-main">
        {/* Header */}
        <Header className="layout-header">
          <div className="header-left">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="collapse-button"
            />
            <Button
              type="primary"
              ghost
              icon={<ArrowLeftOutlined />}
              disabled={!bmpLinked}
              title={bmpLinked ? 'Return to Nexus BMP' : 'No linked Nexus BMP account'}
              onClick={() => { if (bmpLinked) { window.location.href = 'https://nexus.talenthubupskilling.com'; } }}
              className="back-to-bmp-button"
              style={{ marginLeft: 8 }}
            >
              Back to Nexus BMP
            </Button>
          </div>

          <div className="header-right">
            <Space size="middle">
              {/* User Info */}
              <div className="user-info">
                <Text strong>{user?.first_name} {user?.last_name}</Text>
                <div 
                  className="role-badge"
                  style={{ backgroundColor: getRoleBadgeColor(user?.role || '') }}
                >
                  {getRoleDisplayName(user?.role || '')}
                </div>
              </div>
              {/* Notification Bell */}
              <NotificationBell />
              {/* User Avatar & Dropdown */}
              <Dropdown 
                menu={{ items: userMenuItems }} 
                placement="bottomRight"
                trigger={['click']}
              >
                <Avatar 
                  icon={<UserOutlined />} 
                  className="user-avatar"
                  style={{ cursor: 'pointer' }}
                />
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* Main Content */}
        <Content className="layout-content">
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
